import { ClinvarVariantModifier, GenomicQueryService, QueryMetrics, QueryVariant, QueryVariantRangeSchema } from "@gen/lda/v1alpha1/genomicquery_pb"
import { Report, ReportsService } from "@gen/lda/v1alpha1/reports_pb"
import { Sample, SampleService } from "@gen/lda/v1alpha1/sample_pb"

import { create } from "@bufbuild/protobuf";
import { createGrpcWebTransport } from "@connectrpc/connect-web";
import { createClient, Client } from "@connectrpc/connect";
import { PatientDetails, QueryVariantWithClinvar, RawReport } from "./types/genomic";
import { ClinvarResolver } from "./services/ClinvarResolver";

// The report is embedded as an iframe by the Genome Manager UI, which lives on
// region-specific hosts (genome.danteomics.com, genome.lon.danteomics.com, ...).
// gRPC must hit the same region the parent came from. Two deploy modes:
//   1. Path-mounted under genome.*.danteomics.com/clinicalsummary — same-origin
//      with the API. Just use window.location.origin.
//   2. Cross-origin subdomain (clinical-summary-report.genome.danteomics.com) —
//      derive the region from document.referrer.
// The referrer fallback needs an allowlist: document.referrer is set by whoever
// embeds us, so without a check a third-party page could redirect our
// credentialed requests to an attacker-controlled origin.
const ALLOWED_HOSTNAME_SUFFIXES = [".danteomics.com", ".dantelabs.com"];
const DEFAULT_GRPC_BASE_URL = "https://genome.danteomics.com/";

function isAllowedHost(hostname: string): boolean {
    return ALLOWED_HOSTNAME_SUFFIXES.some(
        suffix => hostname === suffix.slice(1) || hostname.endsWith(suffix),
    );
}

function resolveGrpcBaseUrl(): string {
    // Path-mounted deploys (genome.*.danteomics.com/clinicalsummary/...) are
    // same-origin with the gRPC backend. The legacy subdomain
    // (clinical-summary-report.genome.danteomics.com) is NOT — its origin only
    // hosts the SPA, not the gRPC service — so we have to fall through to the
    // referrer trick there.
    if (
        typeof window !== "undefined" &&
        window.location.protocol === "https:" &&
        window.location.pathname.startsWith("/clinicalsummary") &&
        isAllowedHost(window.location.hostname)
    ) {
        return `${window.location.origin}/`;
    }
    const referrer = typeof document !== "undefined" ? document.referrer : "";
    if (!referrer) return DEFAULT_GRPC_BASE_URL;
    try {
        const refUrl = new URL(referrer);
        if (refUrl.protocol !== "https:") return DEFAULT_GRPC_BASE_URL;
        if (!isAllowedHost(refUrl.hostname)) return DEFAULT_GRPC_BASE_URL;
        return `${refUrl.origin}/`;
    } catch {
        return DEFAULT_GRPC_BASE_URL;
    }
}

export class ResolveReport {

    resolvedReport?: Report;
    resolvedVariants?: QueryVariant[];
    resolvedSample?: Sample;
    metrics?: QueryMetrics;
    name: string;

    reportId: number;
    
    reportsService: Client<typeof ReportsService>;
    genomicQueryService: Client<typeof GenomicQueryService>;
    sampleService: Client<typeof SampleService>;
    constructor(reportId: number) {
        this.reportId = reportId
        this.name = `Report-${reportId}`

        
		let grpcTransport = createGrpcWebTransport({
			baseUrl: resolveGrpcBaseUrl(),
			useBinaryFormat: true,
            fetch: (input, init) => {
                return fetch(input, {
                    ...init,
                    credentials: 'include'
                });
            },
            interceptors: [
                
            ]
		});


        this.reportsService = createClient(ReportsService, grpcTransport);
        this.genomicQueryService = createClient(GenomicQueryService, grpcTransport);
        this.sampleService = createClient(SampleService, grpcTransport);
    }

    async getVariants(): Promise<QueryVariantWithClinvar[]> {
        if (this.resolvedVariants) {
            return this.resolvedVariants as QueryVariantWithClinvar[]
        }

        const resolvedReport = await this.getReport()

        const reportMeta = JSON.parse(resolvedReport?.meta || "{}") as RawReport;


        if (!reportMeta?.clinical_summary?.conditions) {
            return []
        }

        const rangeFilters = reportMeta.clinical_summary.conditions.map(condition => 
            create(QueryVariantRangeSchema, {
                filter: {
                    case: "mondo",
                    value: {
                        identifier: {
                            case: "mondoId",
                            value: condition.mondo_id || ""
                        }
                    }
                }
            })
        )

        const variants = await this.genomicQueryService.queryVariants({
            modifier: [
                {
                    modifier: {
                        case: "mondo",
                        value: {}
                    }
                },
                {
                    modifier: {
                        case: "clinvar",
                        value: {}
                    }
                }
            ],
            range: rangeFilters
        })
        
        this.metrics = variants.metrics;
        this.resolvedVariants = variants.variants;

        const clinvarResolver = new ClinvarResolver();
        const resolvedClinvar = await Promise.all(this.resolvedVariants.map(async variant => {
            const clinvarModifier = variant.variantModifier.find(modifier => modifier.modifier.case === "clinvar")?.modifier.value as ClinvarVariantModifier;
            const clinvar = await clinvarResolver.resolveVCV(clinvarModifier.clinvarVcv, clinvarModifier.clinvarAlleleId);
            return {
                ...variant,
                clinvar: clinvar
            }
        }))
        return resolvedClinvar as QueryVariantWithClinvar[]
    }

    async getReport() {
        if (this.resolvedReport) {
            return this.resolvedReport
        }

        const report = await this.reportsService.getReport({
            reportId: this.reportId,
        })
        this.resolvedReport = report.report;
        return report.report;
    }

    async getSample() {
        if (this.resolvedSample) {
            return this.resolvedSample
        }

        const sample = await this.sampleService.getSample({
        })
        this.resolvedSample = sample;
        return sample;
    }


    async getPatient(): Promise<PatientDetails> {
        /*const patient = await this.sampleService.getPatientDetails({
        }).catch(e => {
            console.error(e)
            return {
                patientDetails: {
                    patientName: "Unknown",
                    gender: "Unknown",
                    dateOfBirth: undefined,
                    collectionTime: undefined
                }
            }
        })*/
       
       const mockPatient: PatientDetails = {
        patientName: "Unknown",
        gender: "Unknown",
        dateOfBirth: "Unknown",
        collectionTime: "Unknown"
       }
        return Promise.resolve(mockPatient)
    }
}

/*

export const exampleReport = {
 clinical_summary: {
    conditions: [
        {
            name: "Cystic Fibrosis",
            mondo_id: "MONDO:0009061",
            description: "Cystic fibrosis is a genetic disorder that affects the lungs and digestive system. It is caused by a mutation in the CFTR gene, which is responsible for producing a protein that helps regulate the movement of salt and water in and out of cells.",
            inheritance_pattern: "Autosomal recessive",
            clinical_management_considerations: "...",
            omim_id: "...",
            orphanet_id: "...",
            medgen_id: "...",
            limitations: ["...", "..."],
            associated_genes: ["CFTR"],
        }
    ]
    }
}

export const exampleVariants = {
    "variants": [
      {
        "alternate": [
          "A"
        ],
        "variant_modifier": [
          {
            "mondo": {
              "mondo_id": "MONDO:0009061",
              "pathogenicity": "Pathogenic"
            },
            "modifier": "mondo"
          }
        ],
        "chromosome": "7",
        "position": "117199644",
        "reference": "ATCT",
        "genotype": "HETEROZYGOUS",
        "quality": 40.040000915527344
      }
    ]
  }*/